import { useQueries } from '@tanstack/react-query';

import { useInfiniteConnections } from 'services/repositories/connections/connections';
import { useInfinitePartners, usePartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { formatBasedOnSortArray } from 'support/helpers/arrays/arrays';
import { ORDER_OF_DOCUMENTS } from 'support/helpers/const/const';
import { DocumentType, PartnerDTO, PartnerV2, RelationshipV2 } from 'support/types';
import { ConnectionsFilterKey } from 'pages/Connections/tableConfig';
import { convertToPartnerV2ToRequiredPartnerDTO } from 'pages/TradePartners/NewTradeRequest/NewTradeRequest';

type PartnerForTradeRequest = Pick<PartnerDTO, 'id' | 'documentTypes' | 'logoUrl' | 'name'>;

export type PartnerV2WithRelationships = PartnerV2 & {
  sender_relationships?: Array<RelationshipV2>;
  receiver_relationships?: Array<RelationshipV2>;
};

type ExistingData = {
  partnerForTradeRequest?: PartnerForTradeRequest;
  partnerWithRelationships?: PartnerV2WithRelationships;
  hasFilteredRelationships?: boolean;
};

type UsePartnerToConnectToResult = {
  partner: PartnerForTradeRequest | undefined;
  partnerWithRelationships: PartnerV2WithRelationships | undefined;
  relationships: Array<RelationshipV2>;
  isLoading: boolean;
  isLoadingFullRelationships: boolean;
  isConnected: boolean;
  showNoConnection: boolean;
  isWaitingForPartnerOnboarding: boolean;
};

export const usePartnerToConnectTo = (
  partnerId: string | undefined,
  existingData?: ExistingData,
): UsePartnerToConnectToResult => {
  // Has navigation state data (might be partial if filters were active)
  const cameWithRelationshipData = Boolean(existingData?.partnerWithRelationships);
  const cameWithoutRelationshipData = !cameWithRelationshipData;
  // Has complete data (navigation state + no filters were active)
  const cameWithAllRelationshipData = cameWithRelationshipData && !existingData?.hasFilteredRelationships;

  // 1. Fetch connections if we don't have complete data (or have partial filtered data)
  const { data: connectionsData, isLoading: connectionsLoading } = useInfiniteConnections({
    variables: {
      query: {
        filterValues: partnerId ? [{ key: ConnectionsFilterKey.partnerId, value: partnerId }] : undefined,
        perPage: null,
      },
    },
    enabled: Boolean(partnerId) && !cameWithAllRelationshipData,
  });

  // Prefer fetched data (complete) over existing data (might be partial)
  const fetchedPartner = connectionsData?.pages?.flatMap((page) => page.data).find((p) => p.id === partnerId);
  const partnerFromConnections = fetchedPartner || existingData?.partnerWithRelationships;

  // 2. Derive connection status from presence in connections data
  const isConnected = Boolean(partnerFromConnections);

  // 3. Only fetch partner data if NOT provided via existingData
  const needsPartnerFetch = !existingData?.partnerForTradeRequest && !cameWithAllRelationshipData && Boolean(partnerId);

  const { data: basicPartner, isLoading: basicLoading } = usePartner({
    variables: { id: partnerId },
    enabled: needsPartnerFetch,
  });

  // 4. Only fetch partners search if: no existingPartnerData AND not in connections AND have name
  const needsPartnersSearch =
    needsPartnerFetch && !connectionsLoading && !partnerFromConnections && Boolean(basicPartner?.name);

  const { data: partnersSearchData, isLoading: partnersSearchLoading } = useInfinitePartners({
    variables: {
      query: {
        filterValues: basicPartner?.name ? [{ key: 'name', value: basicPartner.name }] : undefined,
        perPage: '10',
      },
    },
    enabled: needsPartnersSearch,
  });

  const partnerFromSearch = partnersSearchData?.pages?.flatMap((page) => page.items).find((p) => p.id === partnerId);

  // 5. Compute relationships from connected partner data
  const relationships: Array<RelationshipV2> = [
    ...(partnerFromConnections?.receiver_relationships || []),
    ...(partnerFromConnections?.sender_relationships || []),
  ].sort(
    formatBasedOnSortArray(ORDER_OF_DOCUMENTS, (array, value) => array.indexOf(value.message_type as DocumentType)),
  );

  // 6. Prefetch relationship details
  useQueries({
    queries: relationships.map((relationship) => useRelationship.getFetchOptions({ relationshipId: relationship.id })),
  });

  // 7. Derive partner from priority: existingData > connections > search
  const partner: PartnerForTradeRequest | undefined =
    existingData?.partnerForTradeRequest ||
    (partnerFromConnections ? convertToPartnerV2ToRequiredPartnerDTO(partnerFromConnections) : undefined) ||
    partnerFromSearch;

  // 8. Loading state - not loading if we have partial data to show immediately
  const isLoading =
    cameWithoutRelationshipData &&
    (connectionsLoading || (needsPartnerFetch && (basicLoading || (needsPartnersSearch && partnersSearchLoading))));

  // Background loading: we have partial data but are still fetching full data
  const isLoadingFullRelationships = cameWithRelationshipData && !cameWithAllRelationshipData && connectionsLoading;

  // 9. Show no connection state
  const showNoConnection = !isConnected && !isLoading && Boolean(partner);

  // 10. Waiting for partner onboarding
  const isWaitingForPartnerOnboarding = relationships.some(
    (relationship) => relationship.status === 'PARTNER_ONBOARDING_PENDING',
  );

  return {
    partner,
    partnerWithRelationships: partnerFromConnections,
    relationships,
    isLoading,
    isLoadingFullRelationships,
    isConnected,
    showNoConnection,
    isWaitingForPartnerOnboarding,
  };
};
